// @ts-nocheck
/**
 * Marketing Header Component
 * Navigation header for marketing pages using Carbon Design System
 */

import React, { useState } from 'react';
import {
  Header,
  HeaderName,
  HeaderNavigation,
  HeaderMenuItem,
  HeaderMenu,
  HeaderGlobalBar,
  HeaderGlobalAction,
  Button,
  ButtonSet,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import { User, Help, Login } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import { MarketingVariant } from '../../routes/MarketingRouter';
import './MarketingHeader.scss';

interface MarketingHeaderProps {
  variant?: MarketingVariant;
}

const MarketingHeader: React.FC<MarketingHeaderProps> = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const getVariantNavItems = () => {
    const baseItems = [
      { label: 'Features', path: '/features' },
      { label: 'Pricing', path: '/pricing' },
      { label: 'Demo', path: '/demo' },
    ];

    switch (variant) {
      case 'educator':
        return [
          ...baseItems,
          { label: 'For Teachers', path: '/educators' },
          { label: 'Resources', path: '/resources/educators' },
          { label: 'Success Stories', path: '/success-stories/educators' },
        ];
      case 'parent':
        return [
          ...baseItems,
          { label: 'For Families', path: '/parents' },
          { label: 'Safety & Privacy', path: '/privacy' },
          { label: 'Parent Resources', path: '/resources/parents' },
        ];
      case 'administrator':
        return [
          ...baseItems,
          { label: 'For Districts', path: '/administrators' },
          { label: 'Implementation', path: '/implementation' },
          { label: 'Case Studies', path: '/case-studies' },
        ];
      default:
        return [
          ...baseItems,
          {
            label: 'Solutions',
            submenu: [
              { label: 'For Educators', path: '/educators' },
              { label: 'For Parents', path: '/parents' },
              { label: 'For Administrators', path: '/administrators' },
            ],
          },
          { label: 'Resources', path: '/resources' },
          { label: 'About', path: '/about' },
        ];
    }
  };

  const navItems = getVariantNavItems();

  return (
    <Header aria-label="Sigma08 Platform" className="marketing-header">
      <HeaderName
        href="/"
        prefix=""
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          handleNavigation('/');
        }}
      >
        <span className="header-logo">Sigma08</span>
        {variant !== 'default' && (
          <span className="header-variant">for {variant.charAt(0).toUpperCase() + variant.slice(1)}s</span>
        )}
      </HeaderName>

      <HeaderNavigation aria-label="Main navigation">
        {navItems.map((item, index) => {
          if ('submenu' in item && item.submenu) {
            return (
              <HeaderMenu key={index} aria-label={item.label} menuLinkName={item.label}>
                {item.submenu.map((subItem, subIndex) => (
                  <HeaderMenuItem key={subIndex} onClick={() => handleNavigation(subItem.path)}>
                    {subItem.label}
                  </HeaderMenuItem>
                ))}
              </HeaderMenu>
            );
          }
          return (
            <HeaderMenuItem key={index} onClick={() => handleNavigation(item.path)}>
              {item.label}
            </HeaderMenuItem>
          );
        })}
      </HeaderNavigation>

      <HeaderGlobalBar>
        <HeaderGlobalAction aria-label="Help" tooltipAlignment="end" onClick={() => handleNavigation('/help')}>
          <Help size={20} />
        </HeaderGlobalAction>

        <ButtonSet>
          <Button kind="ghost" size="sm" renderIcon={Login} onClick={() => handleNavigation('/auth/login')}>
            Sign In
          </Button>
          <Button kind="primary" size="sm" onClick={() => handleNavigation('/trial')}>
            Start Free Trial
          </Button>
        </ButtonSet>

        {/* Mobile menu */}
        <OverflowMenu className="mobile-menu" aria-label="Mobile menu" flipped renderIcon={() => null}>
          {navItems.map((item, index) => {
            if ('submenu' in item && item.submenu) {
              return item.submenu.map((subItem, subIndex) => (
                <OverflowMenuItem
                  key={`${index}-${subIndex}`}
                  itemText={subItem.label}
                  onClick={() => handleNavigation(subItem.path)}
                />
              ));
            }
            return <OverflowMenuItem key={index} itemText={item.label} onClick={() => handleNavigation(item.path)} />;
          })}
          <OverflowMenuItem itemText="Sign In" onClick={() => handleNavigation('/auth/login')} />
          <OverflowMenuItem itemText="Start Free Trial" onClick={() => handleNavigation('/trial')} />
        </OverflowMenu>
      </HeaderGlobalBar>
    </Header>
  );
};

export default MarketingHeader;
